// ui.js — dida Design-System: Theme-Tokens und gemeinsame UI-Primitive.
// Einzige Quelle für App.js, TrackingScreen.js & künftige Module.
// Gespiegelt als visueller Katalog im claude.ai/design-Projekt "dida".
import { useState, createContext, useContext } from "react";
import { createPortal } from "react-dom";

// ─── THEME ────────────────────────────────────────────────────────────────────
export const ThemeCtx = createContext("glass");
export const useTheme = () => useContext(ThemeCtx);
export const THEMES = {
  // "Hell" — iOS System Light: neutrales Grouped-Grau, weiße Karten,
  // Hairlines statt Rahmen, Translucency in Sidebar/Topbar.
  glass: {
    appBg:"#F2F2F7", appPad:0,
    outerBg:"transparent", outerBorder:"none", outerRadius:"0",
    sidebarBg:"rgba(246,246,248,0.85)", sidebarBorder:"0.5px solid rgba(60,60,67,0.16)",
    topbarBg:"rgba(242,242,247,0.88)", topbarBorder:"0.5px solid rgba(60,60,67,0.14)",
    contentBg:"transparent",
    cardBg:"#FFFFFF", cardBorder:"none", cardRadius:"16px", cardShadow:"0 0 0 0.5px rgba(60,60,67,0.07), 0 1px 2px rgba(0,0,0,0.03)", cardPad:14,
    metBg:"#FFFFFF", metHov:"#F7F7FA", metRadius:"14px",
    metGradUp:"linear-gradient(135deg,#E7F8F1,#F6FCF9)",
    metGradDn:"linear-gradient(135deg,#FDECEA,#FEF6F5)",
    metBorder:"none",
    navActiveBg:"rgba(120,120,128,0.14)", blur:"saturate(180%) blur(20px)",
    textPrimary:"var(--mf-text)", textSecondary:"var(--mf-muted)",
    divider:"rgba(60,60,67,0.14)", inputBg:"rgba(120,120,128,0.09)", inputBorder:"rgba(60,60,67,0.22)", navLabel:"var(--mf-muted)",
    fieldBg:"#FFFFFF", fieldBorder:"rgba(60,60,67,0.24)",
    primary:"#0F6E56", primarySoft:"#E1F5EE", onPrimary:"#ffffff", ring:"rgba(15,110,86,.15)",
    err:"#C93400", errBorder:"rgba(201,52,0,.4)", errRing:"rgba(201,52,0,.14)", errBg:"#FFF4F0",
    neg:"#B3341F", toggleOff:"#E9E9EA",
  },
  // "Dunkel" — iOS System Dark: Schwarz, #1C1C1E-Karten, weiße Labels,
  // Mint als Akzent.
  dark: {
    appBg:"#000000", appPad:0,
    outerBg:"transparent", outerBorder:"none", outerRadius:"0",
    sidebarBg:"rgba(22,22,24,0.88)", sidebarBorder:"0.5px solid rgba(84,84,88,0.42)",
    topbarBg:"rgba(18,18,20,0.88)", topbarBorder:"0.5px solid rgba(84,84,88,0.40)",
    contentBg:"transparent",
    cardBg:"#1C1C1E", cardBorder:"none", cardRadius:"16px", cardShadow:"0 0 0 0.5px rgba(255,255,255,0.055)", cardPad:14,
    metBg:"#1C1C1E", metHov:"#242427", metRadius:"14px",
    metGradUp:"linear-gradient(135deg,rgba(53,201,155,0.20),rgba(53,201,155,0.05))",
    metGradDn:"linear-gradient(135deg,rgba(255,122,112,0.18),rgba(255,122,112,0.05))",
    metBorder:"none",
    navActiveBg:"rgba(120,120,128,0.26)", blur:"saturate(180%) blur(20px)",
    textPrimary:"#FFFFFF", textSecondary:"#98989F",
    divider:"rgba(84,84,88,0.45)", inputBg:"rgba(120,120,128,0.20)", inputBorder:"rgba(84,84,88,0.55)", navLabel:"#6E6E76",
    fieldBg:"#2C2C2E", fieldBorder:"rgba(84,84,88,0.62)",
    primary:"#35C99B", primarySoft:"rgba(53,201,155,.16)", onPrimary:"#06231A", ring:"rgba(53,201,155,.26)",
    err:"#FF8C85", errBorder:"rgba(255,118,110,.55)", errRing:"rgba(255,118,110,.26)", errBg:"rgba(255,118,110,.13)",
    neg:"#FF7A70", toggleOff:"#39393D",
  },
};
// Legacy: gespeicherte "classic"-Einstellungen fallen weich auf Hell zurück
THEMES.classic = THEMES.glass;

// ─── Z-INDEX-EBENEN ───────────────────────────────────────────────────────────
export const Z = {
  sticky: 1,      // fixierte Tabellen-Header
  dropdown: 10,   // Autocomplete-/Suggest-Dropdowns
  overlay: 50,    // Backdrop Ebene 1
  modal: 51,      // Dialoge Ebene 1
  chatOverlay: 52,// Chat-Backdrop (liegt über Ebene 1)
  chat: 53,       // Chat-Fenster
  overlay2: 60,   // Backdrop Ebene 2 (Modal über Modal)
  modal2: 61,     // Dialoge Ebene 2
  menuOverlay: 98,// User-Menü-Backdrop
  menu: 99,       // User-Menü-Popover
  overlay3: 100,  // Backdrop Ebene 3 (höchste)
  modal3: 101,    // Dialoge Ebene 3
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
export const ini = (s) => s.split(/[,\s]+/).filter(Boolean).map(w=>w[0]).join("").slice(0,2).toUpperCase();

// ─── MARKE ────────────────────────────────────────────────────────────────────
// dida-Logo: das d der Wortmarke als Zeichen — Schale und Stamm in Weiß,
// der Mint-Punkt sitzt im Buchstabenauge. Damit ist Mint kein Ornament mehr,
// sondern trägt dieselbe Logik wie der i-Punkt der Wortmarke.
// Flach statt Verlauf: bleibt auch bei 16–24 px scharf (Seitenleiste, Favicon).
export const DidaLogo = ({size=64}) => (
  <svg width={size} height={size} viewBox="0 0 64 64" style={{display:"block",flexShrink:0}}>
    <rect x="1.5" y="1.5" width="61" height="61" rx="15" fill="#0F6E56"/>
    <circle cx="27.5" cy="36" r="11.5" fill="none" stroke="#FFFFFF" strokeWidth="6.5"/>
    <rect x="40" y="14" width="6.5" height="37" rx="3.25" fill="#FFFFFF"/>
    <circle cx="27.5" cy="36" r="4" fill="#35C99B"/>
  </svg>
);

// dida-Wordmark: nur der i-Punkt ist Mint — gebaut aus punktlosem ı (U+0131)
// plus positioniertem Mint-Punkt, damit der Buchstabe selbst in Textfarbe bleibt.
export const DidaWordmark = ({size=14,color="var(--mf-text)"}) => {
  const dot = Math.max(2.5, size*0.19);
  return (
    <span style={{fontSize:size,fontWeight:600,letterSpacing:".01em",color,lineHeight:1,display:"inline-block"}}>
      d<span style={{position:"relative",display:"inline-block"}}>ı<span style={{position:"absolute",left:"50%",transform:"translateX(-50%)",top:size*0.02,width:dot,height:dot,borderRadius:"50%",background:"#35C99B"}}/></span>da
    </span>
  );
};

// ─── PIKTOGRAMME ──────────────────────────────────────────────────────────────
// Eigene Strich-Piktogramme im Markenstil — ersetzen Emojis (🎉 ✉️ ☀️ …).
const PictoSvg = ({size=24,stroke="var(--mf-primary)",children}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{display:"inline-block",verticalAlign:"middle",flexShrink:0}}>{children}</svg>
);
export const Picto = {
  Sunrise: (p) => <PictoSvg {...p}><path d="M4 17h16"/><path d="M7.5 13.5a4.5 4.5 0 0 1 9 0"/><path d="M12 5.5v2.2"/><path d="M5.6 8.6l1.5 1.5"/><path d="M18.4 8.6l-1.5 1.5"/><path d="M8 20.5h8"/></PictoSvg>,
  Sun:     (p) => <PictoSvg {...p}><circle cx="12" cy="12" r="4"/><path d="M12 3.5v2M12 18.5v2M3.5 12h2M18.5 12h2M6 6l1.4 1.4M18 6l-1.4 1.4M6 18l1.4-1.4M18 18l-1.4-1.4"/></PictoSvg>,
  Moon:    (p) => <PictoSvg {...p}><path d="M19.5 14.5A7.8 7.8 0 0 1 9.5 4.5a8 8 0 1 0 10 10z"/></PictoSvg>,
  Check:   (p) => <PictoSvg {...p}><circle cx="12" cy="12" r="9"/><path d="M8.2 12.4l2.6 2.6 5-5.4"/></PictoSvg>,
  Mail:    (p) => <PictoSvg {...p}><rect x="3.5" y="5.5" width="17" height="13" rx="2.5"/><path d="M4.5 7.5l7.5 5.5 7.5-5.5"/></PictoSvg>,
  Spark:   (p) => <PictoSvg {...p}><path d="M12 4.5l1.7 4.6 4.8 1.9-4.8 1.9L12 17.5l-1.7-4.6-4.8-1.9 4.8-1.9z"/><path d="M18.5 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></PictoSvg>,
  Lock:    (p) => <PictoSvg {...p}><rect x="5.5" y="10.5" width="13" height="9" rx="2.5"/><path d="M8.5 10.5v-3a3.5 3.5 0 0 1 7 0v3"/></PictoSvg>,
  Calendar:(p) => <PictoSvg {...p}><rect x="4" y="5.5" width="16" height="14.5" rx="2.5"/><path d="M4 10h16"/><path d="M8.5 3.5v3M15.5 3.5v3"/><path d="M8 13.5h2M13.5 13.5h2M8 16.8h2M13.5 16.8h2"/></PictoSvg>,
  Pin:     (p) => <PictoSvg {...p}><path d="M12 21s-6.5-5.2-6.5-10a6.5 6.5 0 0 1 13 0c0 4.8-6.5 10-6.5 10z"/><circle cx="12" cy="10.6" r="2.3"/></PictoSvg>,
  Phone:   (p) => <PictoSvg {...p}><path d="M6.8 4h2.4l1.3 3.4-1.8 1.4a11.5 11.5 0 0 0 6.5 6.5l1.4-1.8L20 14.8v2.4A2.8 2.8 0 0 1 17.2 20 13.8 13.8 0 0 1 4 6.8 2.8 2.8 0 0 1 6.8 4z"/></PictoSvg>,
  Shield:  (p) => <PictoSvg {...p}><path d="M12 3.5l7 2.6v5.2c0 4.4-2.9 7.6-7 9.2-4.1-1.6-7-4.8-7-9.2V6.1z"/><path d="M9.2 12l2 2 3.6-3.9"/></PictoSvg>,
};

// ─── BASIS-PRIMITIVE ──────────────────────────────────────────────────────────
export const Sp = ({color,children,style={}}) => {
  const dark = useTheme()==="dark";
  const map = dark
    ? { g:{bg:"rgba(53,201,155,.16)",col:"#7DE8C6"}, a:{bg:"rgba(250,199,117,.14)",col:"#F5C97B"}, r:{bg:"rgba(255,118,110,.15)",col:"#FF9E97"}, b:{bg:"rgba(96,165,250,.15)",col:"#93C5FD"}, pu:{bg:"rgba(167,139,250,.15)",col:"#C4B5FD"}, n:{bg:"var(--mf-bg-alt)",col:"var(--mf-muted)"} }
    : { g:{bg:"#E1F5EE",col:"#085041"}, a:{bg:"#FAEEDA",col:"#633806"}, r:{bg:"#FCEBEB",col:"#791F1F"}, b:{bg:"#E6F1FB",col:"#0C447C"}, pu:{bg:"#EEEDFE",col:"#3C3489"}, n:{bg:"var(--mf-bg-alt)",col:"var(--mf-muted)"} };
  const c = map[color]||map.b;
  return <span style={{display:"inline-block",fontSize:9,padding:"1px 6px",borderRadius:20,fontWeight:500,background:c.bg,color:c.col,whiteSpace:"nowrap",...style}}>{children}</span>;
};

export const Av = ({text,bg,col,size=28}) => (
  <div style={{width:size,height:size,borderRadius:"50%",background:bg,color:col,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.36,fontWeight:500,flexShrink:0}}>{text}</div>
);

export const Divider = () => { const t=THEMES[useTheme()]; return <div style={{height:"0.5px",background:t.divider,margin:"7px 0"}} />; };

export const SLabel = ({children}) => { const t=THEMES[useTheme()]; return <div style={{fontSize:9,fontWeight:500,color:t.textSecondary,letterSpacing:".4px",textTransform:"uppercase",margin:"8px 0 4px"}}>{children}</div>; };

export const SH = ({t}) => <div style={{fontSize:10,fontWeight:600,color:"var(--mf-muted)",letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:4,marginTop:10}}>{t}</div>;

export const DRow = ({label,children}) => {
  const t = THEMES[useTheme()];
  return (
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",padding:"3px 0",fontSize:11,gap:8,color:t.textPrimary}}>
      <span style={{color:t.textSecondary,flexShrink:0}}>{label}</span>
      <span style={{fontWeight:500,textAlign:"right"}}>{children}</span>
    </div>
  );
};

export const Btn = ({onClick,color,small,lg,children,style={}}) => {
  const t = THEMES[useTheme()];
  const styles = {
    base:{fontSize:lg?13:small?10:11,padding:lg?"12px 18px":small?"3px 8px":"5px 11px",borderRadius:lg?12:8,fontWeight:lg?600:500,border:`0.5px solid ${t.inputBorder}`,background:t.inputBg,color:t.textPrimary,cursor:"pointer",fontFamily:"inherit",...style},
    g:{background:t.primary,color:t.onPrimary,border:`0.5px solid ${t.primary}`,...(lg?{boxShadow:`0 8px 18px -8px ${t.primary}`}:{})},
    r:{color:t.err,border:`0.5px solid ${t.errBorder}`,background:"transparent"},
    a:{background:"#FAEEDA",color:"#633806",border:"0.5px solid #FAC775"},
  };
  return <button onClick={onClick} style={{...styles.base,...(color?styles[color]:{}),fontFamily:"inherit"}}>{children}</button>;
};

export const Card = ({children,style={}}) => {
  const t = THEMES[useTheme()];
  return <div style={{background:t.cardBg,border:t.cardBorder,borderRadius:t.cardRadius,padding:t.cardPad,boxShadow:t.cardShadow,backdropFilter:t.blur,WebkitBackdropFilter:t.blur,...style}}>{children}</div>;
};

export const CardHead = ({title,icon,action,right}) => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
    <span style={{fontSize:12,fontWeight:500,display:"flex",alignItems:"center",gap:5,color:"var(--mf-text)"}}><i className={`ti ${icon}`} style={{fontSize:13,color:"var(--mf-primary)"}} />{title}</span>
    {right||(action&&<span style={{fontSize:11,color:"var(--mf-primary)",cursor:"pointer"}}>{action}</span>)}
  </div>
);

export const Met = ({v,l,d,dc,clickable,onClick}) => {
  const [hov,setHov] = useState(false);
  const t = THEMES[useTheme()];
  const base = dc==="up"?t.metGradUp:dc==="dn"?t.metGradDn:t.metBg;
  return (
    <div onClick={onClick} onMouseEnter={()=>clickable&&setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{background:hov?t.metHov:base,borderRadius:t.metRadius,padding:"10px 12px",cursor:clickable?"pointer":"default",transition:"background .15s",border:t.metBorder,backdropFilter:t.blur,WebkitBackdropFilter:t.blur}}>
      <div style={{fontSize:18,fontWeight:500,color:t.textPrimary}}>{v}</div>
      <div style={{fontSize:10,color:t.textSecondary,marginTop:1}}>{l}</div>
      {d&&<div style={{fontSize:10,marginTop:3,color:dc==="up"?"var(--mf-primary)":dc==="dn"?"var(--mf-neg)":t.textSecondary}}>{d}</div>}
    </div>
  );
};

export const Tile = ({v,l,d,dc,onClick}) => {
  const [hov,setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{background:hov?"var(--mf-bg-alt)":"var(--mf-bg)",border:`0.5px solid ${hov?"var(--mf-border2)":"var(--mf-border)"}`,borderRadius:14,padding:"11px 13px",cursor:"pointer",transition:"all .15s",position:"relative"}}>
      <div style={{fontSize:18,fontWeight:500,color:"var(--mf-text)"}}>{v}</div>
      <div style={{fontSize:10,color:"var(--mf-muted)",marginTop:1}}>{l}</div>
      {d&&<div style={{fontSize:10,marginTop:3,color:dc==="up"?"var(--mf-primary)":dc==="dn"?"var(--mf-neg)":"var(--mf-muted)"}}>{d}</div>}
      <i className="ti ti-chevron-right" style={{position:"absolute",top:10,right:8,fontSize:11,color:hov?"var(--mf-primary)":"var(--mf-border2)",transition:"color .15s"}}/>
    </div>
  );
};

export const ProgRow = ({label,val,valStr,color="#0F6E56"}) => (
  <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:6}}>
    <span style={{fontSize:10,color:"var(--mf-muted)",minWidth:74}}>{label}</span>
    <div style={{flex:1,height:6,background:"var(--mf-bg-alt)",borderRadius:3,overflow:"hidden"}}>
      <div style={{height:6,width:`${val}%`,background:color,borderRadius:3}} />
    </div>
    <span style={{fontSize:10,fontWeight:500,minWidth:44,textAlign:"right"}}>{valStr}</span>
  </div>
);

export const Insight = ({children,tone="g"}) => {
  const dark = useTheme()==="dark";
  const c = (dark
    ? { g:{bg:"rgba(53,201,155,.12)",col:"#A9EFD7"}, a:{bg:"rgba(250,199,117,.11)",col:"#F2D9A4"}, r:{bg:"rgba(255,118,110,.12)",col:"#FFB9B4"} }
    : { g:{bg:"#E1F5EE",col:"#085041"}, a:{bg:"#FAEEDA",col:"#633806"}, r:{bg:"#FCEBEB",col:"#791F1F"} })[tone];
  return <div style={{padding:"8px 10px",background:c.bg,borderRadius:12,fontSize:10,color:c.col,lineHeight:1.5}}>{children}</div>;
};

export const MiniStat = ({v,l,tone}) => (
  <div style={{background:"var(--mf-bg-alt)",borderRadius:12,padding:"8px 10px"}}>
    <div style={{fontSize:15,fontWeight:600,color:tone==="g"?"var(--mf-primary)":tone==="r"?"var(--mf-neg)":tone==="a"?"#BA7517":"var(--mf-text)"}}>{v}</div>
    <div style={{fontSize:9,color:"var(--mf-muted)",marginTop:1}}>{l}</div>
  </div>
);

// ─── FORMULARE ────────────────────────────────────────────────────────────────
// Modernes Set (claude.ai/design "Formulare"): Floating Labels, 13px-Radien,
// 1.5px-Borders, Fokus-Ringe, Validierungszustände (state="error"|"success" + hint).

export const Input = ({label,value,onChange,placeholder,type="text",state,hint,disabled,style={}}) => {
  const t = THEMES[useTheme()];
  const [focus,setFocus] = useState(false);
  const isErr = state==="error", isOk = state==="success";
  const active = focus || (value!==undefined&&value!==null&&String(value)!=="");
  const trail = isErr?["ti-alert-circle",t.err]:isOk?["ti-circle-check",t.primary]:null;
  const borderC = isErr?t.errBorder:(focus||isOk)?t.primary:t.fieldBorder;
  return (
    <div style={{marginBottom:10,...style}}>
      <div style={{position:"relative"}}>
        <input type={type} value={value} onChange={e=>onChange&&onChange(e.target.value)} placeholder={focus?placeholder:undefined} disabled={disabled}
          onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
          style={{width:"100%",boxSizing:"border-box",fontSize:13,fontWeight:500,color:disabled?t.textSecondary:t.textPrimary,opacity:disabled?.7:1,
            background:isErr?t.errBg:t.fieldBg,border:`1.5px solid ${borderC}`,borderRadius:13,
            padding:label?`19px ${trail?38:13}px 6px 13px`:`12px ${trail?38:13}px 12px 13px`,
            outline:"none",fontFamily:"inherit",transition:"border-color .16s, box-shadow .16s, background .16s",
            boxShadow:focus?`0 0 0 4px ${isErr?t.errRing:t.ring}`:"none"}}/>
        {label&&(active
          ? <label style={{position:"absolute",left:14,top:7,fontSize:10,fontWeight:600,letterSpacing:".02em",color:isErr?t.err:(focus||isOk)?t.primary:t.textSecondary,pointerEvents:"none",transition:"all .16s"}}>{label}</label>
          : <label style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:13,fontWeight:500,color:t.textSecondary,pointerEvents:"none",transition:"all .16s"}}>{label}</label>)}
        {trail&&<i className={`ti ${trail[0]}`} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:15,color:trail[1],pointerEvents:"none"}}/>}
      </div>
      {hint&&<div style={{margin:"5px 2px 0",fontSize:10.5,fontWeight:500,lineHeight:1.4,color:isErr?t.err:isOk?t.primary:t.textSecondary}}>{hint}</div>}
    </div>
  );
};

export const Select = ({label,value,onChange,options,style={}}) => {
  const t = THEMES[useTheme()];
  const [focus,setFocus] = useState(false);
  return (
    <div style={{marginBottom:10,...style}}>
      <div style={{position:"relative"}}>
        <select value={value} onChange={e=>onChange(e.target.value)} onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
          style={{width:"100%",boxSizing:"border-box",fontSize:13,fontWeight:500,color:t.textPrimary,background:t.fieldBg,
            border:`1.5px solid ${focus?t.primary:t.fieldBorder}`,borderRadius:13,
            padding:label?"19px 38px 6px 13px":"12px 38px 12px 13px",outline:"none",
            appearance:"none",WebkitAppearance:"none",MozAppearance:"none",fontFamily:"inherit",cursor:"pointer",
            transition:"border-color .16s, box-shadow .16s",boxShadow:focus?`0 0 0 4px ${t.ring}`:"none"}}>
          {options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
        </select>
        {label&&<label style={{position:"absolute",left:14,top:7,fontSize:10,fontWeight:600,letterSpacing:".02em",color:focus?t.primary:t.textSecondary,pointerEvents:"none",transition:"all .16s"}}>{label}</label>}
        <i className="ti ti-chevron-down" style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:14,color:t.textSecondary,pointerEvents:"none"}}/>
      </div>
    </div>
  );
};

export const Textarea = ({label,value,onChange,rows=3,style={}}) => {
  const t = THEMES[useTheme()];
  const [focus,setFocus] = useState(false);
  const active = focus || (value!==undefined&&value!==null&&String(value)!=="");
  return (
    <div style={{marginBottom:10,...style}}>
      <div style={{position:"relative"}}>
        <textarea value={value} onChange={e=>onChange(e.target.value)} rows={rows}
          onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
          style={{width:"100%",boxSizing:"border-box",fontSize:13,color:t.textPrimary,background:t.fieldBg,
            border:`1.5px solid ${focus?t.primary:t.fieldBorder}`,borderRadius:13,padding:"24px 13px 11px",
            outline:"none",fontFamily:"inherit",resize:"vertical",minHeight:80,lineHeight:1.5,
            transition:"border-color .16s, box-shadow .16s",boxShadow:focus?`0 0 0 4px ${t.ring}`:"none"}}/>
        {label&&(active
          ? <label style={{position:"absolute",left:14,top:7,fontSize:10,fontWeight:600,letterSpacing:".02em",color:focus?t.primary:t.textSecondary,pointerEvents:"none",transition:"all .16s"}}>{label}</label>
          : <label style={{position:"absolute",left:14,top:22,fontSize:13,fontWeight:500,color:t.textSecondary,pointerEvents:"none",transition:"all .16s"}}>{label}</label>)}
      </div>
    </div>
  );
};

export const SearchInput = ({value,onChange,placeholder,style={}}) => {
  const t = THEMES[useTheme()];
  const [focus,setFocus] = useState(false);
  return (
    <div style={{position:"relative",...style}}>
      <i className="ti ti-search" style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",fontSize:14,color:t.textSecondary,pointerEvents:"none"}}/>
      <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
        onFocus={()=>setFocus(true)} onBlur={()=>setFocus(false)}
        style={{width:"100%",boxSizing:"border-box",fontSize:12.5,color:t.textPrimary,background:t.fieldBg,
          border:`1.5px solid ${focus?t.primary:t.fieldBorder}`,borderRadius:999,padding:"10px 15px 10px 38px",
          outline:"none",fontFamily:"inherit",transition:"all .16s",boxShadow:focus?`0 0 0 4px ${t.ring}`:"none"}}/>
    </div>
  );
};

export const Toggle = ({val,onChange,small}) => {
  const t = THEMES[useTheme()];
  const w=small?40:51, h=small?24:31, k=small?20:27, pad=2;
  return (
    <div onClick={()=>onChange(!val)} style={{width:w,height:h,borderRadius:h/2,background:val?t.primary:t.toggleOff,cursor:"pointer",position:"relative",flexShrink:0,transition:"background .18s"}}>
      <div style={{position:"absolute",top:(h-k)/2,left:val?w-k-pad:pad,width:k,height:k,borderRadius:"50%",background:"#fff",transition:"left .18s",boxShadow:"0 1px 3px rgba(0,0,0,.3)"}}/>
    </div>
  );
};

export const SegControl = ({value,onChange,options}) => {
  const t = THEMES[useTheme()];
  return (
    <div style={{display:"flex",background:"var(--mf-bg-alt)",borderRadius:12,padding:3,gap:3}}>
      {options.map((o,i)=>(
        <button key={o} onClick={()=>onChange(i)} style={{flex:1,border:"none",cursor:"pointer",borderRadius:10,padding:"7px 6px",fontSize:11.5,fontWeight:600,fontFamily:"inherit",transition:"all .15s",
          background:i===value?t.cardBg:"transparent",color:i===value?t.textPrimary:t.textSecondary,boxShadow:i===value?"0 1px 4px rgba(0,0,0,.14)":"none",outline:i===value?`0.5px solid ${t.inputBorder}`:"none"}}>{o}</button>
      ))}
    </div>
  );
};

export const RadioGroup = ({value,onChange,options}) => {
  const t = THEMES[useTheme()];
  return (
    <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
      {options.map(o=>{
        const v=o.value??o, l=o.label??o, on=value===v;
        return (
          <label key={v} onClick={()=>onChange(v)} style={{display:"flex",alignItems:"center",gap:7,cursor:"pointer"}}>
            <span style={{width:17,height:17,borderRadius:"50%",border:`1.5px solid ${on?t.primary:t.fieldBorder}`,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s",flexShrink:0}}>
              {on&&<span style={{width:8,height:8,borderRadius:"50%",background:t.primary}}/>}
            </span>
            <span style={{fontSize:11.5,color:t.textPrimary}}>{l}</span>
          </label>
        );
      })}
    </div>
  );
};

export const Checkbox = ({checked,onChange,label}) => {
  const t = THEMES[useTheme()];
  return (
    <label onClick={e=>{e.preventDefault();onChange(!checked);}} style={{display:"flex",alignItems:"flex-start",gap:9,cursor:"pointer"}}>
      <span style={{width:19,height:19,flexShrink:0,borderRadius:6,border:`1.5px solid ${checked?t.primary:t.fieldBorder}`,background:checked?t.primary:"transparent",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s",marginTop:1}}>
        {checked&&<i className="ti ti-check" style={{fontSize:12,color:t.onPrimary}}/>}
      </span>
      <span style={{fontSize:11.5,color:t.textPrimary,lineHeight:1.4}}>{label}</span>
    </label>
  );
};

export const FilterPill = ({active,onClick,children}) => {
  const t = THEMES[useTheme()];
  return <button onClick={onClick} style={{fontSize:10,padding:"3px 11px",borderRadius:20,border:`0.5px solid ${active?t.primary:t.inputBorder}`,background:active?t.primary:"transparent",color:active?t.onPrimary:t.textSecondary,cursor:"pointer",fontFamily:"inherit",fontWeight:active?500:400,transition:"all .15s"}}>{children}</button>;
};

export const Pill = ({active,onClick,children}) => {
  const t = THEMES[useTheme()];
  return <button onClick={onClick} style={{border:`1.5px solid ${active?t.primary:t.fieldBorder}`,background:active?t.primarySoft:"transparent",color:active?t.primary:t.textSecondary,borderRadius:999,padding:"6px 13px",fontSize:11.5,fontWeight:600,cursor:"pointer",fontFamily:"inherit",transition:"all .15s"}}>{children}</button>;
};

// ─── OVERLAYS ─────────────────────────────────────────────────────────────────
// Portal an document.body: backdrop-filter auf Vorfahren (Glas-Theme) macht diese
// sonst zum Containing Block für position:fixed — Modals wären dann verschoben.
// data-theme-Wrapper noetig: document.body liegt ausserhalb des Theme-Containers,
// sonst bekaemen portalierte Modals im Dark Mode die Classic-CSS-Variablen.
export const Portal = ({children}) => createPortal(<div data-theme={useTheme()}>{children}</div>, document.body);

export const Modal = ({open,onClose,title,children,width=400}) => {
  if(!open) return null;
  return (
    <Portal>
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.35)",zIndex:Z.overlay,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"var(--mf-bg)",border:"0.5px solid var(--mf-border2)",borderRadius:14,boxShadow:"0 24px 64px rgba(0,0,0,.3)",padding:16,width,maxWidth:"95%",maxHeight:"90%",overflowY:"auto",position:"relative"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:500}}>{title}</div>
          <div onClick={onClose} style={{cursor:"pointer",fontSize:18,color:"var(--mf-muted)",lineHeight:1}}>×</div>
        </div>
        {children}
      </div>
    </div>
    </Portal>
  );
};

export const Overlay = ({title,sub,icon,iconBg="#E1F5EE",iconCol="#085041",onClose,children,width=640}) => (
  <Portal>
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:Z.overlay}}/>
    <div style={{position:"fixed",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width,maxHeight:"82vh",maxWidth:"96vw",background:"var(--mf-bg)",borderRadius:14,zIndex:Z.modal,display:"flex",flexDirection:"column",overflow:"hidden",boxShadow:"0 24px 64px rgba(0,0,0,.3)"}}>
      <div style={{padding:"14px 18px",borderBottom:"0.5px solid var(--mf-border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <div style={{width:36,height:36,borderRadius:8,background:iconBg,display:"flex",alignItems:"center",justifyContent:"center"}}><i className={`ti ${icon}`} style={{fontSize:18,color:iconCol}}/></div>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:600,color:"var(--mf-text)"}}>{title}</div>
          {sub&&<div style={{fontSize:11,color:"var(--mf-muted)"}}>{sub}</div>}
        </div>
        <div onClick={onClose} style={{cursor:"pointer",fontSize:20,color:"var(--mf-muted)",width:28,height:28,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",background:"var(--mf-bg-alt)"}}>×</div>
      </div>
      <div style={{padding:"14px 18px",flex:1,overflowY:"auto"}}>{children}</div>
    </div>
  </Portal>
);

// ─── TOASTS ───────────────────────────────────────────────────────────────────
// Leichtgewichtiges Feedback für Schreiboperationen: toast("…","r"|"g").
// ToastHost einmal im App-Root mounten.
let toastListeners = [];
export const toast = (msg, tone = "g") => {
  toastListeners.forEach(fn => fn({ id: Date.now() + Math.random(), msg, tone }));
};

export function ToastHost() {
  const [items, setItems] = useState([]);
  useState(() => {
    const fn = (t) => {
      setItems(p => [...p, t]);
      setTimeout(() => setItems(p => p.filter(x => x.id !== t.id)), 4200);
    };
    toastListeners.push(fn);
    return true;
  });
  if (!items.length) return null;
  const tones = { g: { bg: "#0F6E56", col: "#E1F5EE", icon: "ti-circle-check" }, r: { bg: "#B42318", col: "#FEF5F4", icon: "ti-alert-triangle" } };
  return (
    <Portal>
      <div style={{ position: "fixed", bottom: 18, right: 18, zIndex: 300, display: "flex", flexDirection: "column", gap: 8, maxWidth: 340 }}>
        {items.map(t => {
          const tn = tones[t.tone] || tones.g;
          return (
            <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, background: tn.bg, color: tn.col, padding: "10px 14px", borderRadius: 10, fontSize: 12, fontWeight: 500, boxShadow: "0 8px 24px rgba(0,0,0,.25)" }}>
              <i className={`ti ${tn.icon}`} style={{ fontSize: 15, flexShrink: 0 }} />
              {t.msg}
            </div>
          );
        })}
      </div>
    </Portal>
  );
}

// ─── TABELLEN ─────────────────────────────────────────────────────────────────
export const Tbl = ({heads,children}) => (
  <table style={{width:"100%",borderCollapse:"collapse"}}>
    <thead><tr>{heads.map(h=><th key={h} style={{textAlign:"left",color:"var(--mf-muted)",fontWeight:400,fontSize:10,padding:"4px 6px",borderBottom:"0.5px solid var(--mf-border)"}}>{h}</th>)}</tr></thead>
    <tbody>{children}</tbody>
  </table>
);

export const Td = ({children,bold,color,style={}}) => (
  <td style={{padding:"6px 6px",borderBottom:"0.5px solid var(--mf-border)",fontSize:11,fontWeight:bold?500:400,color:color||"var(--mf-text)",...style}}>{children}</td>
);

// ─── CHARTS (SVG, ohne Dependencies) ──────────────────────────────────────────
export const LineChart = ({data,color="#0F6E56",h=70,xL,xR,unit=""}) => {
  if(!data||data.length<2) return null;
  const mn=Math.min(...data), mx=Math.max(...data), range=mx-mn||1, w=280;
  const pts=data.map((v,i)=>`${(i/(data.length-1))*w},${h-(((v-mn)/range)*(h-14)+7)}`).join(" ");
  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{display:"block"}}>
        <polygon points={`${pts} ${w},${h} 0,${h}`} fill={color} fillOpacity={0.12}/>
        <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5}/>
      </svg>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"var(--mf-muted)",marginTop:2}}>
        <span>{xL} · {data[0]}{unit}</span><span>{xR} · {data[data.length-1]}{unit}</span>
      </div>
    </div>
  );
};

export const Donut = ({segs,size=120,stroke=18,centerV,centerL}) => {
  const r=(size-stroke)/2, C=2*Math.PI*r, total=segs.reduce((s,x)=>s+x.v,0)||1;
  let acc=0;
  return (
    <div style={{display:"flex",alignItems:"center",gap:14}}>
      <svg width={size} height={size} style={{flexShrink:0}}>
        <g transform={`rotate(-90 ${size/2} ${size/2})`}>
          {segs.map((seg,i)=>{
            const frac=seg.v/total, off=acc; acc+=frac;
            return <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={seg.c} strokeWidth={stroke}
              strokeDasharray={`${frac*C} ${C}`} strokeDashoffset={-off*C}/>;
          })}
        </g>
        {centerV&&<text x="50%" y="48%" textAnchor="middle" style={{fontSize:15,fontWeight:600,fill:"var(--mf-text)"}}>{centerV}</text>}
        {centerL&&<text x="50%" y="62%" textAnchor="middle" style={{fontSize:8,fill:"var(--mf-muted)"}}>{centerL}</text>}
      </svg>
      <div style={{display:"flex",flexDirection:"column",gap:4,flex:1}}>
        {segs.map((seg,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:10}}>
            <span style={{width:8,height:8,borderRadius:2,background:seg.c,flexShrink:0}}/>
            <span style={{color:"var(--mf-muted)",flex:1}}>{seg.l}</span>
            <span style={{fontWeight:500,color:"var(--mf-text)"}}>{seg.vl||seg.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const StackedBars = ({rows,h=110,legend}) => {
  const max = Math.max(...rows.map(r=>r.parts.reduce((s,p)=>s+p.v,0)))||1;
  return (
    <div>
      <div style={{display:"flex",alignItems:"flex-end",gap:6,height:h}}>
        {rows.map((row,i)=>{
          const total = row.parts.reduce((s,p)=>s+p.v,0);
          return (
            <div key={i} style={{flex:1,display:"flex",flexDirection:"column",justifyContent:"flex-end",height:"100%",gap:0}} title={`${row.label}: ${total}`}>
              {row.parts.map((p,j)=>(
                <div key={j} style={{height:`${(p.v/max)*100}%`,background:p.c,borderRadius:j===0?"3px 3px 0 0":0,minHeight:p.v>0?2:0}}/>
              ))}
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",gap:6,marginTop:3}}>
        {rows.map((row,i)=><div key={i} style={{flex:1,fontSize:8,color:"var(--mf-muted)",textAlign:"center"}}>{row.label}</div>)}
      </div>
      {legend&&<div style={{display:"flex",gap:12,marginTop:6,flexWrap:"wrap"}}>
        {legend.map((lg,i)=>(
          <span key={i} style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:"var(--mf-muted)"}}>
            <span style={{width:8,height:8,borderRadius:2,background:lg.c}}/>{lg.l}
          </span>
        ))}
      </div>}
    </div>
  );
};

export const HBar = ({label,value,max,color="#0F6E56",valStr}) => (
  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
    <div style={{width:96,fontSize:11,color:"var(--mf-text)",flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{label}</div>
    <div style={{flex:1,height:14,background:"var(--mf-bg-alt)",borderRadius:4,overflow:"hidden"}}>
      <div style={{height:14,background:color,borderRadius:4,width:`${Math.min(100,(value/max)*100)}%`}}/>
    </div>
    <div style={{fontSize:10,fontWeight:500,minWidth:62,textAlign:"right",color:"var(--mf-text)"}}>{valStr??value}</div>
  </div>
);
